import {assert, assertNotNull} from '@one-click-lca/3d-buildings-shared';
import {Coordinates3D} from '@one-click-lca/3d-buildings-shared/features';
import {clamp} from '@one-click-lca/3d-buildings-shared/math';
import {
  screenPosToObjectsIntersection as screenPosToObjectsIntersectionLib,
  screenNDCPosToObjectsIntersection as screenNDCPosToObjectsIntersectionLib,
  getOrthographicRelativeDistance,
  getPerspectiveRelativeDistance,
  turnObjectTowardCamera,
} from '@one-click-lca/3d-buildings-shared/rendering';
import {hasOwnProperty} from '@one-click-lca/3d-buildings-shared/utils';
import {Tween, Easing} from '@tweenjs/tween.js';
import * as THREE from 'three';

import eventEmitter from '../../../event_emitter';
import Events from '../../../events';

import Camera from './camera';
import {INVISIBLE_MATERIAL} from './materials';

export type SingleMatMesh<T extends THREE.Material = THREE.Material> =
  THREE.Mesh<THREE.BufferGeometry, T>;

function isShared(obj: unknown) {
  return (
    hasOwnProperty(obj, 'userData') &&
    hasOwnProperty(obj.userData, 'shared') &&
    obj.userData.shared === true
  );
}

export function colorToVec3(color: number): THREE.Vector3 {
  return new THREE.Vector3(
    ((color & 0xff0000) >> 16) / 0xff,
    ((color & 0x00ff00) >> 8) / 0xff,
    (color & 0x0000ff) / 0xff,
  );
}

/**
 * Copied from three.js because it's not exported
 *
 * @param c component in sRGB color space, between 0 and 1
 * @returns component in linear color space, between 0 and 1
 */
export function SRGBToLinear(c: number): number {
  return c < 0.04045
    ? c * 0.0773993808
    : Math.pow(c * 0.9478672986 + 0.0521327014, 2.4);
}

//list of all possible map attributes that can be found in threejs materials, based on the doc of r95
const threeMapAttributes = [
  'alphaMap',
  'aoMap',
  'bumpMap',
  'displacementMap',
  'emissiveMap',
  'envMap',
  'gradientMap',
  'lightMap',
  'map',
  'metalnessMap',
  'normalMap',
  'roughnessMap',
  'specularMap',
];

export function releaseObject(object3d: THREE.Object3D): void {
  object3d.traverse(child => {
    if (child && !isShared(child)) {
      //several objects that do not share a base class (Mesh, Line...) may have resources to release
      if (
        hasOwnProperty(child, 'geometry') &&
        child.geometry instanceof THREE.BufferGeometry &&
        !isShared(child.geometry)
      ) {
        child.geometry.dispose();
        child.geometry = null;
        delete child.geometry;
      }

      if (hasMaterials(child)) {
        forEachMaterial(child, material => {
          if (isShared(material)) {
            return;
          }
          for (const attr of threeMapAttributes) {
            if (hasOwnProperty(material, attr) && !isShared(material[attr])) {
              const matAttribute = material[attr];
              if (
                matAttribute instanceof THREE.Texture ||
                matAttribute instanceof THREE.WebGLRenderTarget
              ) {
                matAttribute.dispose();
                material[attr] = null;
              }
            }
          }
          if (material instanceof THREE.ShaderMaterial) {
            for (const uniform of Object.values(material.uniforms)) {
              if (
                hasOwnProperty(uniform, 'value') &&
                (uniform.value instanceof THREE.Texture ||
                  uniform.value instanceof THREE.WebGLRenderTarget) &&
                !isShared(uniform.value)
              ) {
                uniform.value.dispose();
                uniform.value = null;
              }
            }
          }
          material.dispose();
        });
      }
    }
  });
  object3d.children = [];
}

export const RENDER_ORDER = {
  CLIPPING: 100,
  QUOTES: 200,
  MEASURE: 300,
};

// From THREE.ExtrudeGeometry.WorldUVGenerator
export const BuildingUVGenerator: THREE.UVGenerator = {
  generateTopUV(): THREE.Vector2[] {
    return [new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2()];
  },

  generateSideWallUV(
    _geometry,
    vertices,
    indexA,
    indexB,
    indexC,
    indexD,
  ): THREE.Vector2[] {
    const a = new THREE.Vector3(
      vertices[indexA * 3],
      vertices[indexA * 3 + 1],
      vertices[indexA * 3 + 2],
    );
    const b = new THREE.Vector3(
      vertices[indexB * 3],
      vertices[indexB * 3 + 1],
      vertices[indexB * 3 + 2],
    );
    const c = new THREE.Vector3(
      vertices[indexC * 3],
      vertices[indexC * 3 + 1],
      vertices[indexC * 3 + 2],
    );
    const d = new THREE.Vector3(
      vertices[indexD * 3],
      vertices[indexD * 3 + 1],
      vertices[indexD * 3 + 2],
    );
    const magSide = a.clone().sub(b.clone()).length();

    return [
      new THREE.Vector2(0, a.z),
      new THREE.Vector2(magSide, b.z),
      new THREE.Vector2(magSide, c.z),
      new THREE.Vector2(0, d.z),
    ];
  },
};

let _cachedBuildingTexture: HTMLCanvasElement;

export function generateBuildingSideTexture(): HTMLCanvasElement {
  if (!_cachedBuildingTexture) {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('No 2D context available');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, 32, 32);
    context.fillStyle = '#aaa';
    context.fillRect(0, 1, 32, 1);
    _cachedBuildingTexture = canvas;
  }
  return _cachedBuildingTexture;
}

export function deserializeQuaternion(q: {
  _x?: number;
  _y?: number;
  _z?: number;
  _w?: number;
}): THREE.Quaternion {
  return new THREE.Quaternion(q._x, q._y, q._z, q._w);
}

export function threeColor(value: number): THREE.Color {
  return new THREE.Color(value);
}

function _randomInt(max: number) {
  return Math.floor(Math.random() * max);
}

export function getRandomColorN(
  n = _randomInt(20),
  avoidRed = false,
  saturation = 0.8,
  lightness = 0.7,
): THREE.Color {
  // Based on http://martin.ankerl.com/2009/12/09/how-to-create-random-colors-programmatically/
  const goldenRatioConj = 0.618033988749895;
  let hue = (n * goldenRatioConj) % 1;
  if (avoidRed) {
    hue = hue / 1.25 + 0.1;
  }
  return new THREE.Color().setHSL(hue, saturation, lightness);
}

export function getRandomHTMLColorN(
  n = _randomInt(20),
  avoidRed = false,
  saturation = 0.8,
  lightness = 0.7,
): string {
  return getRandomColorN(n, avoidRed, saturation, lightness).getStyle();
}

export function getHTMLPrescriptionColor(type: string | null): string {
  let color = 'black';
  if (type) {
    const indices = type.match(/\d+/);
    if (indices?.length) {
      const colorIndex = Number(indices[0]); // extract a numeric value from the string
      if (Number.isFinite(colorIndex)) {
        color = getRandomHTMLColorN(colorIndex, false, 0.7, 0.5);
      }
    }
  }
  return color;
}

export function freeWebglContext(renderer: THREE.WebGLRenderer): void {
  //Due to how resource management works in threejs
  //(resources keep a reference to the renderer for deallocation)
  //if a resource used by the renderer is not freed (because it is used elsewhere)
  //the reference will prevent the renderer (and its webgl context) from being GCed
  //Browsers can only have a limited number of webgl contexts,
  //So at some point the oldest one is recycled (usually the main one used for the app)
  //to prevent this problem, we have 3 solutions :
  //1. never share resources between renderers and clone/manage everything. Performance hit and hard to get right
  //2. Use the same renderer for everything. WebGL being a state machine, a misconfiguration between uses is highly likely
  //3. Forcefully dispose of the context.
  //We'll use solution 3, which is listed in MDN webgl best practices :
  //https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/WebGL_best_practices#lose_contexts_eagerly
  //Note that the context JS object will always exist due to the reference being held to it,
  //but at least it's not active anymore so won't provoke the recycling of the main app one.
  renderer.forceContextLoss();
}

/**
 * @returns Data URL of the screenshot of the scene using the camera passed in parameter
 */
export function createSnapshot(
  scene: THREE.Scene,
  camera: THREE.Camera,
  width: number,
  height: number,
  type = 'image/jpeg',
  clippingEnabled = false,
): string {
  const snapshotRenderer = new THREE.WebGLRenderer({
    antialias: true,
    stencil: clippingEnabled,
  });
  snapshotRenderer.localClippingEnabled = clippingEnabled;
  snapshotRenderer.shadowMap.enabled = true;
  snapshotRenderer.shadowMap.type = THREE.PCFSoftShadowMap;
  snapshotRenderer.setClearColor(0xffffff);
  snapshotRenderer.setPixelRatio(window.devicePixelRatio);
  snapshotRenderer.setSize(
    width / window.devicePixelRatio,
    height / window.devicePixelRatio,
  );
  snapshotRenderer.render(scene, camera);
  const dataURL = snapshotRenderer.domElement.toDataURL(type);
  freeWebglContext(snapshotRenderer);
  return dataURL;
}

function intersectionFilter(obj: THREE.Object3D): boolean {
  // We don't want to pick the edges
  return !obj.userData.noPicking;
}

/**
 *
 * @param screenNDCPos Position on the screen in Normalized Device Coordinates
 * @param recursive Whether or not to intersect children of objects passed in parameter
 *
 * @returns the intersected object
 */
export function screenNDCPosToObjectsIntersection(
  camera: Camera,
  screenNDCPos: THREE.Vector2,
  objectsToTest: Array<THREE.Object3D>,
  recursive = false,
): THREE.Intersection<THREE.Object3D> | null {
  return screenNDCPosToObjectsIntersectionLib(
    camera.getThreeCamera(),
    screenNDCPos,
    objectsToTest,
    intersectionFilter,
    recursive,
  );
}

/**
 *
 * @param domElt the DOM element containing the rendered 3D
 * @param screenPos Position on the screen in pixels
 * @param recursive Whether or not to intersect children of objects passed in parameter
 *
 * @returns the intersected object
 */
export function screenPosToObjectsIntersection(
  domElt: Element,
  camera: Camera,
  screenPos: THREE.Vector2,
  objectsToTest: Array<THREE.Object3D>,
  recursive = false,
): THREE.Intersection<THREE.Object3D> | null {
  return screenPosToObjectsIntersectionLib(
    domElt,
    camera.getThreeCamera(),
    screenPos,
    objectsToTest,
    intersectionFilter,
    recursive,
  );
}

export function bufferAttributeToVec3Array(
  bufferAttribute: THREE.BufferAttribute,
): Array<THREE.Vector3> {
  return bufferAttributeToCoords3DArray(bufferAttribute).map(
    coords => new THREE.Vector3(coords[0], coords[1], coords[2]),
  );
}

export function bufferAttributeToCoords3DArray(
  bufferAttribute: THREE.BufferAttribute,
): Coordinates3D[] {
  assert(bufferAttribute.itemSize === 3, 'Wrong buffer attribute item size');
  const array: Coordinates3D[] = [];
  for (let index = 0; index < bufferAttribute.count; ++index) {
    array.push([
      bufferAttribute.getX(index),
      bufferAttribute.getY(index),
      bufferAttribute.getZ(index),
    ]);
  }
  return array;
}

export function bufferAttributeToFlatArray(
  bufferAttribute: THREE.BufferAttribute,
): number[] {
  const array = [];
  for (let index = 0; index < bufferAttribute.count; ++index) {
    for (let i = 0; i < bufferAttribute.itemSize; ++i) {
      array.push(bufferAttribute.getComponent(index, i));
    }
  }
  return array;
}

/**
 * @returns The intersection (if it exists) of the screen position
 * with a plane facing the camera and at the position of the mesh
 */
export function screenNDCPosToMeshPlaneIntersection(
  mesh: THREE.Mesh,
  screenNDCPos: THREE.Vector2,
  camera: Camera,
): THREE.Vector3 | null {
  const worldViewDirection = camera
    .getThreeCamera()
    .getWorldDirection(new THREE.Vector3());
  const planeNormal = new THREE.Vector3(
    -worldViewDirection.x,
    -worldViewDirection.y,
    0,
  );
  planeNormal.normalize();
  const planeGeom = new THREE.PlaneGeometry(100000, 100000, 1, 1);
  const planeMaterial = new THREE.MeshBasicMaterial({
    side: THREE.DoubleSide,
    visible: true,
  });
  const planeMesh = new THREE.Mesh(planeGeom, planeMaterial);
  mesh.getWorldPosition(planeMesh.position);
  planeMesh.quaternion.copy(camera.quaternion);
  planeMesh.updateMatrix();
  planeMesh.updateMatrixWorld();
  const intersection = screenNDCPosToObjectsIntersection(camera, screenNDCPos, [
    planeMesh,
  ]);
  releaseObject(planeMesh);
  return intersection ? intersection.point : null;
}

export function tweenObjectZScale(
  object: THREE.Object3D,
  targetValue: number,
  time: number,
  completionCallback: () => void,
): void {
  new Tween(object.scale)
    .to({z: targetValue}, time)
    .easing(Easing.Quadratic.Out)
    .onUpdate(() => {
      object.updateMatrix();
      eventEmitter.emit(Events.RenderingStateChanged);
    })
    .onComplete(() => {
      eventEmitter.emit(Events.RenderingStateChanged);
      completionCallback();
    })
    .start();
}

export function tweenNumber<AttrName extends string>(
  obj: Record<AttrName, number>,
  attrName: AttrName,
  targetValue: number,
  time: number,
  easing = Easing.Quadratic.Out,
  updateCallback = () => {
    return;
  },
): Promise<void> {
  let promise = Promise.resolve();
  if (time) {
    promise = new Promise(resolve => {
      new Tween(obj)
        .to({[attrName]: targetValue}, time)
        .easing(easing)
        .onUpdate(updateCallback)
        .onComplete(() => {
          resolve();
        })
        .start();
    });
  }
  return promise;
}

export function tweenMaterialOpacity(
  material: THREE.Material,
  targetValue: number,
  time: number,
): void {
  if (targetValue !== 1) {
    material.transparent = true;
    material.needsUpdate = true;
  }
  new Tween(material)
    .to({opacity: targetValue}, time)
    .easing(Easing.Quadratic.Out)
    .onUpdate(() => {
      eventEmitter.emit(Events.RenderingStateChanged);
    })
    .onComplete(() => {
      if (targetValue === 1) {
        material.transparent = false;
        material.needsUpdate = true;
      }
      eventEmitter.emit(Events.RenderingStateChanged);
    })
    .start();
}

/**
 * Computes the bounding box of a THREE.Object3D in its local space.
 * This is by opposition to Box3.expandByObject which takes into account all parents, which may sometimes not be desirable
 *
 * @returns The bounding box of obj and all its recursive children in obj local space
 */
export function computeLocalBoundingBox(obj: THREE.Object3D): THREE.Box3 {
  const bbox = new THREE.Box3();
  const recExpand = (
    child: THREE.Object3D,
    parentTransfo: THREE.Matrix4,
  ): void => {
    child.updateMatrix();
    const childTransfo = new THREE.Matrix4().multiplyMatrices(
      parentTransfo,
      child.matrix,
    );
    if (
      child instanceof THREE.Mesh &&
      child.geometry instanceof THREE.BufferGeometry
    ) {
      child.geometry.computeBoundingBox();
      //should be assigned by computeBoundingBox but TS doesn't know that
      assertNotNull(child.geometry.boundingBox);
      const transformedBox = child.geometry.boundingBox
        .clone()
        .applyMatrix4(childTransfo);
      bbox.union(transformedBox);
    }
    for (const grandChild of child.children) {
      recExpand(grandChild, childTransfo);
    }
  };
  recExpand(obj, new THREE.Matrix4());
  return bbox;
}

export function changeMaterialOpacity(
  material: THREE.Material,
  opacity: number,
): void {
  material.userData.savedOpacity = material.opacity;
  material.userData.savedTransparency = material.transparent;
  material.opacity = opacity;
  material.transparent = opacity < 1;
  material.needsUpdate = true;
}

export function hasOpacityChanged(material: THREE.Material): boolean {
  const {savedOpacity, savedTransparency} = material.userData;
  //those can be false/0 so we have to use !==
  return (
    typeof savedOpacity === 'number' &&
    Number.isFinite(savedOpacity) &&
    typeof savedTransparency === 'boolean'
  );
}

export function restoreMaterialOpacity(material: THREE.Material): void {
  if (hasOpacityChanged(material)) {
    const {savedOpacity, savedTransparency} = material.userData;
    material.opacity = savedOpacity;
    material.transparent = savedTransparency;
    material.needsUpdate = true;
    delete material.userData.savedTransparency;
    delete material.userData.savedOpacity;
  }
}

export function shallowObject3dCopy(
  obj: THREE.Object3D,
  copyParent: THREE.Object3D,
  mat?: THREE.Material,
): THREE.Object3D {
  let copy;
  if (obj instanceof THREE.Group) {
    copy = new THREE.Group();
  } else if (obj instanceof THREE.Scene) {
    copy = new THREE.Scene();
  } else if (obj instanceof THREE.Mesh) {
    copy = new THREE.Mesh(obj.geometry, mat ?? obj.material);
  } else {
    throw new Error('Unhandled object type to copy');
  }
  copyParent.add(copy);
  copy.position.copy(obj.position);
  copy.rotation.copy(obj.rotation);
  copy.scale.copy(obj.scale);
  copy.updateMatrix();
  copy.updateMatrixWorld();
  return copy;
}

/**
 * THREE doesn't allow 3D objects to have multiple parents
 * however materials and geometries can be shared by multiple 3D objects
 * This function is here to allow a recursive shallow copy
 * allowing copying 3D objects only (which are light compared to geometries/materials with texture)
 *
 * @param obj The object to copy from
 * @param copy The object to shallow copy into
 * @param getMaterial Function returning a material to associate to the copy (potentially modifying it)
 * @param copyObject Whether or not to copy the object (defaults to true)
 */
export function recursiveShallowCopy(
  obj: THREE.Object3D,
  copy: THREE.Object3D,
  getMaterial: (obj: THREE.Object3D) => THREE.Material | undefined,
  copyObject: (obj: THREE.Object3D) => boolean = () => true,
): void {
  for (const child of obj.children) {
    if (copyObject(child)) {
      const childCopy = shallowObject3dCopy(child, copy, getMaterial(child));
      if (
        childCopy instanceof THREE.Group ||
        childCopy instanceof THREE.Scene
      ) {
        recursiveShallowCopy(child, childCopy, getMaterial, copyObject);
      }
    }
  }
}

/**
 * recursively activate shadows on a 3D object, with an optional test for filtering
 */
export function activateShadows(
  obj: THREE.Object3D,
  shouldActivate: (child: THREE.Object3D) => boolean = () => true,
): void {
  obj.traverse(child => {
    if (shouldActivate(child)) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
}

/**
 * Similar to addProperty, but for THREE.3DObject userData
 */
export function addUserData<
  F extends {userData: {[key: string]: unknown}},
  P extends Record<PropertyKey, unknown>,
>(f: F, data: P): F & {userData: P} {
  const userData = Object.assign(f.userData, data);
  return Object.assign(f, {userData});
}

export function hasMaterial(arg: unknown): arg is {material: THREE.Material} {
  //THREE.LineSegments can have materials and they are not sub classes of THREE.Mesh
  return (
    hasOwnProperty(arg, 'material') && arg.material instanceof THREE.Material
  );
}

export type ObjectWithMaterials = {
  material: THREE.Material | Array<THREE.Material>;
};

export function hasMaterials(obj: unknown): obj is ObjectWithMaterials {
  return (
    hasOwnProperty(obj, 'material') &&
    (obj.material instanceof THREE.Material ||
      (Array.isArray(obj.material) &&
        obj.material.every(mat => mat instanceof THREE.Material)))
  );
}

export function hasMaterialWithColor(
  arg: unknown,
): arg is {material: THREE.Material & {color: THREE.Color}} {
  return (
    hasMaterial(arg) &&
    hasOwnProperty(arg.material, 'color') &&
    arg.material.color instanceof THREE.Color
  );
}

export function readRenderTargetPixels(
  renderer: THREE.WebGLRenderer,
  renderTarget: THREE.WebGLRenderTarget,
  componentsPerPixel = 4,
): Uint8Array {
  const {width, height} = renderTarget;
  const pixels = new Uint8Array(width * height * componentsPerPixel);
  renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixels);
  return pixels;
}

//as of THREE R110, onBeforeRender is not called on THREE.Group
//if we want to apply some logic to a group of objects before rendering,
//the workaround will be to instead use a mesh that renders nothing on the screen
//and add the other objects as children of this mesh, making it semantically a group
export function createFakeGroup(): THREE.Mesh {
  const fakeGroup = new THREE.Mesh(
    new THREE.BoxGeometry(1),
    INVISIBLE_MATERIAL,
  );
  fakeGroup.userData.noEdges = true;
  fakeGroup.userData.noPicking = true;
  return fakeGroup;
}
export const RELATIVE_SMALL_TEXT_SIZE = 0.015;
export const RELATIVE_MEDIUM_TEXT_SIZE = 0.02;
export const RELATIVE_LARGE_TEXT_SIZE = 0.03;

const MIN_SIZE_SCALING_FACTOR = 30;
const MAX_SIZE_SCALING_FACTOR = 100;

export function computeCameraDependantSize(
  camera: THREE.Camera,
  referencePoint: THREE.Vector3,
  relativeSize: number,
): number {
  let size = 1;
  if (camera instanceof THREE.OrthographicCamera) {
    size = getOrthographicRelativeDistance(camera, relativeSize, true);
  } else if (camera instanceof THREE.PerspectiveCamera) {
    size = getPerspectiveRelativeDistance(
      referencePoint,
      camera,
      relativeSize,
      true,
    );
  }
  return clamp(
    size,
    relativeSize * MIN_SIZE_SCALING_FACTOR,
    relativeSize * MAX_SIZE_SCALING_FACTOR,
  );
}

export function scaleBeforeRender(
  obj: THREE.Object3D,
  referencePoint: THREE.Vector3,
  relativeSize: number,
): THREE.Object3D['onBeforeRender'] {
  return (_renderer, _scene, camera) => {
    const scale = computeCameraDependantSize(
      camera,
      referencePoint,
      relativeSize,
    );
    obj.scale.set(scale, scale, scale);
    obj.updateMatrix();
    obj.updateMatrixWorld();
  };
}

export function createScalingGroup(
  referencePoint: THREE.Vector3,
  relativeSize: number,
): THREE.Mesh {
  const scalingGroup = createFakeGroup();
  scalingGroup.onBeforeRender = scaleBeforeRender(
    scalingGroup,
    referencePoint,
    relativeSize,
  );
  return scalingGroup;
}

export function createCameraFacingGroup(): THREE.Mesh {
  const cameraFacingGroup = createFakeGroup();
  cameraFacingGroup.onBeforeRender = (_renderer, _scene, camera) => {
    turnObjectTowardCamera(cameraFacingGroup, camera);
  };
  return cameraFacingGroup;
}

/**
 * Check a condition on all materials of an object
 */
export function checkMaterials<T>(
  mesh: ObjectWithMaterials,
  checkCb: (materials: Array<THREE.Material>) => T,
): T {
  if (mesh.material instanceof THREE.Material) {
    return checkCb([mesh.material]);
  } else {
    return checkCb(mesh.material);
  }
}

/**
 * Apply the same callback to all materials of an object, independently of each other
 */
export function forEachMaterial(
  mesh: ObjectWithMaterials,
  cb: (material: THREE.Material) => void,
): void {
  checkMaterials(mesh, materials => {
    for (const material of materials) {
      cb(material);
    }
  });
}
